import React from "react";
import { AspectRatio, Box, Card, Flex, Heading, Icon, Image, Link as ChakraLink, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import {
  LuArrowRight,
  LuStar,
} from "react-icons/lu";

import { Button } from "../../../components/ui/Button";
import VideoPrueba from "../../../assets/video.mp4";
import BackgroundRegistro from "../../../assets/BackgroundRegistro.jpg";
import CampFoto from "../../../assets/Camp.jpg";
import Diversion from "../../../assets/diversion.jpg";
import Formacion from "../../../assets/formacion.jpg";
import AmistadesCard from "../../../assets/amistadesCard.jpg";
import WakoWelcome from "../../../assets/mascots/wako/Welcome.svg";
import ArawaksMark from "../../../assets/Logo.svg";
import {
  FloatingStat,
  ImageStoryCard,
  SectionEyebrow,
  SectionHeading,
  SectionShell,
} from "./components";

const clubValues = [
  {
    image: Diversion,
    title: "Diversión",
    subtitle: "Inolvidable",
    accent: "purple.300",
    position: "40% center",
  },
  {
    image: AmistadesCard,
    title: "Amistades",
    subtitle: "De por vida",
    accent: "brand.red",
    position: "center 40%",
  },
  {
    image: Formacion,
    title: "Formación",
    subtitle: "Humana y cristiana",
    accent: "green.300",
    position: "60% center",
  },
];

const Hero = () => (
  <Box
    as="section"
    position="relative"
    overflow="hidden"
    bg="public.bg.canvas"
    bgImage={{
      _light: "radial-gradient(circle at 82% 22%, #f8dadd 0, transparent 27%), linear-gradient(180deg, #fffafa 0%, #f1f3f6 82%)",
      _dark: "radial-gradient(circle at 82% 22%, #4b252d 0, transparent 29%), linear-gradient(180deg, #21191d 0%, #171a21 82%)",
    }}
    color="public.fg.default"
    pt={{ base: "104px", md: "136px" }}
    pb={{ base: 16, md: 24 }}
  >
    <Image
      src={ArawaksMark}
      alt=""
      aria-hidden="true"
      position="absolute"
      w={{ base: "320px", md: "520px" }}
      left={{ base: "-190px", md: "-250px" }}
      bottom={{ base: "-130px", md: "-220px" }}
      opacity={{ _light: ".045", _dark: ".09" }}
      transform="rotate(-12deg)"
    />

    <SimpleGrid position="relative" zIndex="1" maxW="7xl" mx="auto" px={{ base: 5, md: 10 }} columns={{ base: 1, lg: 2 }} gap={{ base: 12, lg: 16 }} alignItems="center">
      <Stack gap="6" align="start" py={{ lg: 8 }}>
        <SectionEyebrow>Más que un club, una familia</SectionEyebrow>
        <Heading as="h1" fontFamily="display" fontSize={{ base: "56px", sm: "68px", md: "82px" }} lineHeight=".88" letterSpacing="-.045em">
          <Box as="span" display="block">Club</Box>
          <Box as="span" display="block" color="brand.red">Arawaks.</Box>
        </Heading>
        <Text fontSize={{ base: "lg", md: "xl" }} lineHeight="1.7" color="public.fg.muted" maxW="590px">
          Una comunidad de familias en Monterrey donde los niños crecen entre aventuras, amistades y valores que dejan huella.
        </Text>

        <Flex gap="2" wrap="wrap">
          {["Diversión", "Amistad", "Formación"].map((value) => (
            <Flex key={value} align="center" gap="2" px="3.5" py="2" rounded="full" bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.subtle" fontSize="sm" fontWeight="800">
              <Box boxSize="2" rounded="full" bg="brand.red" />
              {value}
            </Flex>
          ))}
        </Flex>

        <Stack direction={{ base: "column", sm: "row" }} w={{ base: "full", sm: "auto" }} gap="4">
          <Button as={RouterLink} to="/registro" color="red" size="lg">Iniciar mi registro <Icon as={LuArrowRight} boxSize="4" /></Button>
          <Button as={RouterLink} to="/actividades" variant="outline" color="dark" size="lg">Conocer actividades</Button>
        </Stack>
      </Stack>

      <Box position="relative" minH={{ base: "460px", md: "610px" }} maxW="610px" w="full" mx="auto">
        <Box
          position="absolute"
          inset={{ base: "28px 8px 34px 24px", md: "36px 4px 44px 42px" }}
          rounded={{ base: "52px 18px 52px 18px", md: "78px 24px 78px 24px" }}
          bg="brand.red"
          transform="rotate(2.5deg)"
          shadow="0 26px 70px rgba(127,29,29,.28)"
        />
        <Box
          position="absolute"
          inset={{ base: "18px 18px 50px 8px", md: "22px 32px 58px 18px" }}
          rounded={{ base: "46px 16px 46px 16px", md: "68px 20px 68px 20px" }}
          overflow="hidden"
          bg="gray.900"
          borderWidth={{ base: "5px", md: "8px" }}
          borderColor="public.bg.canvas"
          shadow="0 24px 70px rgba(45,55,72,.22)"
        >
          <Box as="video" autoPlay loop muted playsInline w="full" h="full" objectFit="cover" poster={CampFoto} onLoadedMetadata={(event) => { event.currentTarget.currentTime = 10; }}><source src={VideoPrueba} type="video/mp4" /></Box>
          <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(20,5,8,.48), transparent 52%)" />
        </Box>

        <Flex position="absolute" zIndex="3" top={{ base: 0, md: "-4px" }} left={{ base: 0, md: "-20px" }} align="center" gap="3" p="2" pr="4" rounded="full" bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" shadow="cardLift">
          <Box display="grid" placeItems="center" boxSize={{ base: "11", md: "13" }} p="2" rounded="full" bg="white">
            <Image src={ArawaksMark} alt="Símbolo del Club Arawaks" maxH="full" />
          </Box>
          <Stack gap="0">
            <Text fontFamily="display" fontWeight="700" lineHeight="1">Club Arawaks</Text>
            <Text color="public.fg.subtle" fontSize="10px" fontWeight="800" letterSpacing="widest">MONTERREY</Text>
          </Stack>
        </Flex>

        <Image src={WakoWelcome} alt="Wako te da la bienvenida al Club Arawaks" position="absolute" zIndex="4" w={{ base: "152px", md: "215px" }} right={{ base: "-8px", md: "-24px" }} bottom="0" filter="drop-shadow(0 18px 18px rgba(127,29,29,.2))" />
        <FloatingStat icon={LuStar} value="+60 años" label="Haciendo camino" position="absolute" zIndex="3" left={{ base: 0, md: "-18px" }} bottom={{ base: "18px", md: "34px" }} />
      </Box>
    </SimpleGrid>
  </Box>
);

const KnowTheClub = () => (
  <SectionShell bg="public.bg.panel" position="relative" overflow="hidden">
    <Box position="absolute" boxSize="260px" rounded="full" bg="#dce8f1" _dark={{ bg: "#243746" }} left="-150px" top="15%" opacity=".5" />
    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 10, lg: 16 }} alignItems="center">
      <Stack gap="6" p={{ base: 0, lg: 8 }} rounded="32px" position="relative">
        <SectionHeading
          eyebrow="Conoce al Club Arawaks"
          title="Una iniciativa de familias, para familias."
          description="El Club Arawaks es una iniciativa de padres de familia preocupados por la formación de sus hijos y otros jóvenes, ubicado en Monterrey, México."
        />
        <Text color="public.fg.muted" fontSize="lg" lineHeight="1.8">
          Nuestra misión es ser un medio formidable para aprovechar al máximo el tiempo libre de los hijos, a través de actividades variadas y divertidas, aunadas a una sólida formación humana y cristiana.
        </Text>
        <Stack direction={{ base: "column", sm: "row" }} gap="4" align={{ base: "stretch", sm: "center" }}>
          <Button as={RouterLink} to="/nosotros" variant="solid" color="red" size="lg">
            Nuestra historia
            <Icon as={LuArrowRight} boxSize="4" />
          </Button>
          <Button as={RouterLink} to="/actividades" variant="ghost" color="red" size="lg">
            Ver actividades
          </Button>
        </Stack>
      </Stack>

      <Box position="relative" minH={{ base: "420px", md: "520px" }}>
        <Box
          position="absolute"
          top="0"
          left="0"
          w="74%"
          h="68%"
          rounded="48px 18px 48px 18px"
          bgImage={`url(${CampFoto})`}
          bgSize="cover"
          bgPosition="center"
          shadow="cardLift"
        />
        <Box
          position="absolute"
          right="0"
          bottom="0"
          w="56%"
          h="54%"
          rounded="18px 42px 18px 42px"
          bgImage={`url(${Formacion})`}
          bgSize="cover"
          bgPosition="center"
          shadow="cardLift"
          borderWidth="6px"
          borderColor="public.bg.panel"
        />
        <FloatingStat icon={LuStar} value="+60 años" label="Haciendo camino" position="absolute" left="0" bottom={{ base: "8", md: "12" }} />
      </Box>
    </SimpleGrid>
  </SectionShell>
);

const History = () => (
  <SectionShell bg="public.bg.canvas" position="relative" overflow="hidden">
    <Box position="absolute" w="360px" h="150px" rounded="full" bg="#eadde8" _dark={{ bg: "#3d3040" }} right="-170px" bottom="5%" transform="rotate(18deg)" opacity=".48" />
    <SimpleGrid position="relative" zIndex="1" columns={{ base: 1, lg: 2 }} gap={{ base: 10, lg: 14 }} alignItems="center">
      <Stack gap="6">
        <SectionHeading
          eyebrow="Nuestra historia · Haciendo camino"
          title="Más de 60 años formando líderes."
          description="Durante más de 60 años, decenas de familias han encontrado en el Club Arawaks un medio formidable para aprovechar al máximo el tiempo libre de los hijos."
        />
        <Text color="public.fg.muted" fontSize="lg" lineHeight="1.8">
          El Club Arawaks se suma a las numerosas iniciativas alrededor del mundo promovidas bajo el impulso y las enseñanzas de San Josemaría Escrivá de Balaguer, fundador del{" "}
          <ChakraLink href="https://opusdei.org/es-mx/" target="_blank" color="brand.redFg" fontWeight="800">
            Opus Dei
          </ChakraLink>
          .
        </Text>
        <Button as={RouterLink} to="/nosotros" variant="ghost" color="red" alignSelf="start">
          Leer más
          <Icon as={LuArrowRight} boxSize="4" />
        </Button>
      </Stack>

      <Card.Root overflow="hidden" rounded="32px 14px 32px 14px" bg="public.bg.elevated" borderWidth="8px" borderColor="public.bg.elevated" shadow="cardLift">
        <AspectRatio ratio={16 / 9}>
          <Box
            as="iframe"
            title="Video de historia del Club Arawaks"
            src="https://www.youtube.com/embed/flm0wxgF0eE?si=n3FuhMIPKFJuj7Dy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            border="0"
          />
        </AspectRatio>
      </Card.Root>
    </SimpleGrid>
  </SectionShell>
);

const WhyArawaks = () => (
  <SectionShell bg="public.bg.panel">
    <Stack gap={{ base: 8, md: 12 }}>
      <Box display="flex" justifyContent="center" sx={{ "& > *": { alignItems: "center" } }}>
        <SectionHeading
          eyebrow="¿Por qué Club Arawaks?"
          title="Una experiencia completa."
          description="Formamos niños con valores, amistades y recuerdos que duran para siempre."
          align="center"
        />
      </Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 5, md: 7 }} alignItems="stretch">
        {clubValues.map((item) => (
          <ImageStoryCard key={item.title} {...item} />
        ))}
      </SimpleGrid>
    </Stack>
  </SectionShell>
);

const JoinAdventure = () => (
  <Box as="section" bg="public.bg.canvas" px={{ base: 5, md: 10 }} py={{ base: 16, md: 24 }}>
    <Box position="relative" overflow="hidden" maxW="6xl" mx="auto" rounded={{ base: "32px 14px 32px 14px", md: "56px 20px 56px 20px" }} bg="gray.950">
    <Box position="absolute" inset="0" bgImage={`url(${BackgroundRegistro})`} bgSize="cover" bgPosition="center" opacity="0.32" />
    <Box position="absolute" inset="0" bg="rgba(10,0,0,.74)" />
    <Box position="absolute" boxSize="250px" rounded="full" bg="red.500" right="-110px" top="-130px" opacity=".22" />
    <Stack position="relative" zIndex="1" maxW="4xl" mx="auto" px={{ base: 6, md: 12 }} py={{ base: 16, md: 20 }} textAlign="center" align="center" gap="6" color="white">
      <Box display="flex" justifyContent="center">
        <SectionEyebrow tone="dark">Únete a la aventura</SectionEyebrow>
      </Box>
      <Heading as="h2" fontFamily="display" fontSize={{ base: "4xl", md: "6xl" }} lineHeight="1.02">
        ¿Listo para la aventura?
      </Heading>
      <Text color="whiteAlpha.800" fontSize={{ base: "lg", md: "xl" }} lineHeight="1.7" maxW="2xl">
        Únete a una comunidad de familias que eligen el campamento, la naturaleza y la amistad de por vida.
      </Text>
      <Stack direction={{ base: "column", sm: "row" }} gap="4" justify="center">
        <Button as={RouterLink} to="/registro" variant="solid" color="red" size="lg">
          Registrarme ahora
          <Icon as={LuArrowRight} boxSize="4" />
        </Button>
        <Button as={RouterLink} to="/contacto" variant="ghost" color="transparent" size="lg">
          Hablar con nosotros
        </Button>
      </Stack>
    </Stack></Box>
  </Box>
);

const Inicio = () => {
  return (
    <Box bg="public.bg.canvas">
      <Hero />
      <KnowTheClub />
      <History />
      <WhyArawaks />
      <JoinAdventure />
    </Box>
  );
};

export default Inicio;
